import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import './Register.css';
import ParachuteAnimation from '../components/Parachute';

const Register = () => {
  // User details
  const [userType, setUserType] = useState('job-seeker');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dob, setDOB] = useState('');
  const [gender, setGender] = useState('male');
  
  // Company details
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [numOfEmployees, setNumOfEmployees] = useState('');
  const [employeeRange, setEmployeeRange] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [countrySuggestions, setCountrySuggestions] = useState([]);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
  const [countryId, setCountryId] = useState('');
  const [location, setLocation] = useState(null);
  const [postalCode, setPostalCode] = useState('');
  const [isHeadquarters, setIsHeadquarters] = useState(false);
  const [activeSection, setActiveSection] = useState("user-details");
  const [success, setSuccess] = useState('');
  const [industry, setIndustry] = useState(null);

  const [twitterUrl, setTwitterUrl] = useState('');
  const [linkedinUrl, setLinkedInUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');

  const [industryData, setIndustryData] = useState([]);
  
  // Form state
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [citySearchLoading, setCitySearchLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.search-input-container')) {
        setShowCitySuggestions(false);
        setShowCountrySuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchIndustries = async () => {
      try {      
        const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/list-industries`);
  
        if (response.ok) {
          const data = await response.json();
          setIndustryData(data);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to fetch industries');
        }
      } catch (error) {
        setError('Failed to fetch industries');
        console.error('Error fetching industries:', error);
      } 
      // finally {
      //   setLoading(false);
      // }
    };
  
    fetchIndustries();
  }, []);

  const handleNextSection = () => {
    // Validate user details before moving to company details
    if (!firstName || !lastName || !email || !phoneNumber || !password || !confirmPassword || !dob || !gender) {
      setError('All user fields are required');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Phone validation
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    if (!phoneRegex.test(phoneNumber.replace(/[-\s()]/g, ''))) {
      setError('Please enter a valid phone number');
      return;
    }
    
    // Clear any previous errors
    setError('');
    setActiveSection('company-details');
  };
  
  const handlePreviousSection = () => {
    setActiveSection('user-details');
  };  // Handle city search

  
  // Handle country search
  useEffect(() => {
    const searchCity = async () => {
      if (citySearch.length < 2) {
        setCitySuggestions([]);
        return;
      }
      
      setCitySearchLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/api/city/search?search_term=${encodeURIComponent(citySearch)}&limit=10`);
        
        if (response.ok) {
          const data = await response.json();
          setCitySuggestions(data);
        } else {
          console.error('Error fetching city suggestions:', response.statusText);
          setCitySuggestions([]);
        }
      } catch (err) {
        console.error('Error fetching city suggestions:', err);
      } finally {
        setCitySearchLoading(false);
      }
    };
    
    const timeoutId = setTimeout(searchCity, 300);
    return () => clearTimeout(timeoutId);
  }, [citySearch]);

  const handleCitySelect = (cityData) => {
    setCity(cityData.city_name);
    setCitySearch(cityData.city_name);
    setCountry(cityData.name);
    setCountryId(cityData.country_id);
    setShowCitySuggestions(false);
    setLocation(cityData);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Reset error state
    setError('');
    setIsLoading(true);
    
    try {
      
      // Validate user details
      if (!firstName || !lastName || !email || !phoneNumber || !password || !confirmPassword) {
        throw new Error('All user fields are required');
      }
      
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }
      
      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }
      
      // Phone validation
      const phoneRegex = /^\+?[0-9]{10,15}$/;
      if (!phoneRegex.test(phoneNumber.replace(/[-\s()]/g, ''))) {
        throw new Error('Please enter a valid phone number');
      }
      
      // Validate company details
      if (!companyName || !companyDescription || !companyWebsite) {
        throw new Error('Company name, description, and website are required');
      }
      
      if (!streetAddress || !city || !state || !country || !postalCode) {
        throw new Error('All company address fields are required');
      }
      
      if (!numOfEmployees || !employeeRange) {
        throw new Error('Number of employees and employee range are required');
      }

      if (!industry ) {
        throw new Error('Company industry needs to be selected');
      }
      
      // Website validation
      const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!urlRegex.test(companyWebsite)) {
        throw new Error('Please enter a valid website URL');
      }
      
      if (!agreeTerms) {
        throw new Error('You must agree to the Terms and Privacy Policy');
      }
      
      // For demo purposes, let's check for a specific test case
      if (email === 'test@exists.com') {
        throw new Error('This email is already registered. Please use a different email or log in.');
      }
      
      // Prepare data for API call
      const userData = {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        bio: "",
        profile_picture_url: "",
        phone_number: phoneNumber,
        user_type: "platform_company",
        dob: dob,
        gender: gender
      };
      
      const companyData = {
        name: companyName,
        description: companyDescription,
        website: companyWebsite,
        logo_url: companyLogoUrl,
        num_of_employees: parseInt(numOfEmployees),
        employee_range: employeeRange,
        street_address: streetAddress,
        city: location.city_id,
        state,
        country: location.country_id,
        postal_code: postalCode,
        is_headquarters: isHeadquarters,
        twitter_url: twitterUrl,
        linkedin_url: linkedinUrl,
        facebook_url: facebookUrl,
        industry: industry,
        images: []
      };
      
      const requestData = {
        user: userData,
        company: companyData,
      };
      
    //   In a real application, you would make an API call here
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.log(errorData.detail);
        throw new Error(errorData.message || 'Registration failed. Please try again.');
      }
      
      console.log('Registration data:', requestData);
      
      setSuccess('Registration success, redirecting to login');
      setTimeout(() => {
        navigate('/login');;
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-left">
        <div className="logo-container">
          <img src="/logo.png" alt="JobHuntly" className="logo" />
        </div>
        
        <ParachuteAnimation />
        
        <div className="stats-container">
          <div className="stats-graphic">
            <div className="stats-bar"></div>
            <div className="stats-bar"></div>
            <div className="stats-bar"></div>
            <div className="stats-bar"></div>
          </div>
          <h2 className="stats-number">100K+</h2>
          <p className="stats-text">People got hired</p>
        </div>

      </div>
      
      <div className="register-right">
        
        <div className="register-form-container">
          <h1 className="welcome-text">Create an Account</h1>
        
          
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

        {success && (
            <div className="success-message">
              <span className="error-icon">✅</span>
              {success}
            </div>
          )}
          
          <form onSubmit={handleRegister} className="register-form">
            {/* User Details Section */}
            {activeSection === "user-details" && (
              <>
                <h2 className="section-title">User Details</h2>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input 
                      type="text" 
                      id="firstName" 
                      placeholder="Enter your first name" 
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input 
                      type="text" 
                      id="lastName" 
                      placeholder="Enter your last name" 
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                      <label>Date of birth</label>
                      <input
                        type="date"
                        value={dob}
                        onChange={(e) => setDOB(e.target.value)}
                        max={new Date().toISOString().split("T")[0]}
                        required
                      />
                  </div>
                  
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                    >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input 
                    type="email" 
                    id="email" 
                    placeholder="Enter email address" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input 
                    type="tel" 
                    id="phoneNumber" 
                    placeholder="Enter phone number" 
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input 
                    type="password" 
                    id="password" 
                    placeholder="Create a password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <p className="password-hint">Password must be at least 8 characters</p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input 
                    type="password" 
                    id="confirmPassword" 
                    placeholder="Confirm your password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <button 
                  type="button" 
                  className="next-button"
                  onClick={handleNextSection}
                >
                  Next: Company Details
                </button>
              </>
            )}

            {/* Company Details Section */}
            {activeSection === "company-details" && (
              <>
                <h2 className="section-title">Company Details</h2>
                
                <div className="form-group">
                  <label htmlFor="companyName">Company Name</label>
                  <input 
                    type="text" 
                    id="companyName" 
                    placeholder="Enter company name" 
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="companyDescription">Company Description</label>
                  <textarea 
                    id="companyDescription" 
                    placeholder="Enter company description" 
                    value={companyDescription}
                    onChange={(e) => setCompanyDescription(e.target.value)}
                    required
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="companyWebsite">Company Website</label>
                  <input 
                    type="url" 
                    id="companyWebsite" 
                    placeholder="https://example.com" 
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="companyLogo">Company Logo URL</label>
                  <input 
                    type="url" 
                    id="companyLogo" 
                    placeholder="https://example.com/logo.png" 
                    value={companyLogoUrl}
                    onChange={(e) => setCompanyLogoUrl(e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                      <label htmlFor="industry">Select Industry</label>
                      <select
                        id="industry"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        required
                      >
                        <option value="">Select range</option>
                        {industryData.map((ind) => (
                          <option value={ind.id}>{ind.name}</option>
                        ))}
                      </select>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="numOfEmployees">Number of Employees</label>
                    <input 
                      type="number" 
                      id="numOfEmployees" 
                      placeholder="e.g. 100" 
                      value={numOfEmployees}
                      onChange={(e) => setNumOfEmployees(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="employeeRange">Employee Range</label>
                    <select
                      id="employeeRange"
                      value={employeeRange}
                      onChange={(e) => setEmployeeRange(e.target.value)}
                      required
                    >
                      <option value="">Select range</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="501-1000">501-1000</option>
                      <option value="1000+">1001+</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="twitter">Twitter</label>
                    <input 
                      type="text" 
                      id="twitter" 
                      placeholder="http://twitter.com/company" 
                      value={twitterUrl}
                      onChange={(e) => setTwitterUrl(e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="linkedinurl">Linkedin</label>
                    <input 
                      type="text" 
                      id="linkedinurl" 
                      placeholder="http://linkedin.com/company" 
                      value={linkedinUrl}
                      onChange={(e) => setLinkedInUrl(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="facebookurl">Facebook</label>
                    <input 
                      type="text" 
                      id="facebookurl" 
                      placeholder="http://linkedin.com/company" 
                      value={facebookUrl}
                      onChange={(e) => setFacebookUrl(e.target.value)}
                    />
                  </div>
                </div>
                
                <h2 className="section-title">Company Address</h2>

                <div className="form-group">
                  <label htmlFor="streetAddress">Street Address</label>
                  <input 
                    type="text" 
                    id="streetAddress" 
                    placeholder="Enter street address" 
                    value={streetAddress}
                    onChange={(e) => setStreetAddress(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group search-input-container">
                  <label htmlFor="city">City</label>
                  <input 
                    type="text" 
                    id="city" 
                    placeholder="Search for city" 
                    value={citySearch}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setShowCitySuggestions(true);
                    }}
                    onFocus={() => setShowCitySuggestions(true)}
                    required
                  />
                  {citySearchLoading && (
                    <div className="search-loading">Searching...</div>
                  )}
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <ul className="suggestions-list">
                      {citySuggestions.map((suggestion, index) => (
                        <li 
                          key={index}
                          onClick={() => handleCitySelect(suggestion)}
                        >
                          {suggestion.city_name}, {suggestion.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="state">State/Province</label>
                  <input 
                    type="text" 
                    id="state" 
                    placeholder="Enter state or province" 
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <input 
                    type="text" 
                    id="country" 
                    placeholder="Country will be auto-filled when you select a city" 
                    value={country}
                    disabled
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="postalCode">Postal Code</label>
                  <input 
                    type="text" 
                    id="postalCode" 
                    placeholder="Enter postal code" 
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-checkbox">
                  <input 
                    type="checkbox" 
                    id="headquarters" 
                    checked={isHeadquarters}
                    onChange={(e) => setIsHeadquarters(e.target.checked)}
                  />
                  <label htmlFor="headquarters">This is the company headquarters</label>
                </div>
                
                <div className="form-checkbox">
                  <input 
                    type="checkbox" 
                    id="terms" 
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                  />
                  <label htmlFor="terms">
                    I agree to the <a href="#" className="terms-link">Terms</a> and <a href="#" className="terms-link">Privacy Policy</a>
                  </label>
                </div>
                
                <div className="form-buttons">
                  <button 
                    type="button" 
                    className="back-button"
                    onClick={handlePreviousSection}
                  >
                    Back
                  </button>
                  
                  <button 
                    type="submit" 
                    className={`register-button ${isLoading ? 'loading' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        Creating Account...
                      </>
                    ) : 'Create Account'}
                  </button>
                </div>
              </>
            )}
          </form>
          
          <div className="login-link">
            Already have an account? <Link to="/login">Log In</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;